import { ICartItem, IStaffInventoryItems } from 'experiments/StaffLink/StaffLink.types'
import { DimensionData } from 'services/Api/BillingApiFolder.ts/type'
import {
  billingRoundFunction,
  chooseBestOffer,
  chooseMaxFreeItems,
  convertPaisaToRupee,
  convertRupeeToPaisa,
  handleOfferInPayload
} from 'state/billing/billing.helpers'
import { NewItemDetailEntity } from 'state/billing/billing.types'
import { getTaxComponents } from 'state/new-summary/orders/orders.formatter'
import { OrderItem } from 'state/new-summary/orders/orders.types'

export const getStaffFormattedItemsPayload = (
  itemDetails: (NewItemDetailEntity & {
    inventoryItemId?: string
    qtt_props?: DimensionData
  })[],
  allItems: Array<OrderItem['addedItemsItem']>,
  customerId: string,
  isIGST: boolean
) => {
  return itemDetails.map((item) => {
    const allItemDetails = allItems.find(
      (storeItem: OrderItem['addedItemsItem']) => storeItem.catalogueItemId === item.itemId
    ) as OrderItem['addedItemsItem']

    const { appliedOfferId = '', applicableOfferDetails = [] } = item

    const { unitOffers } = handleOfferInPayload({
      applicableOfferDetails,
      appliedOfferId,
      customerId,
      item: {
        discountInRupee: item.discount || 0,
        rateInRupee: item.rate || 0,
        quantity: item.quantity
      }
    })

    const hasFreeUnits =
      item.applicableOfferDetails?.some(
        (off) => off.info && 'free_units_on_min_purchase' in off.info
      ) || false

    let freeUnits = 0
    if (hasFreeUnits) {
      const { maxFreeUnits } = chooseMaxFreeItems(item.applicableOfferDetails, item.quantity, {
        customerId
      } as any)
      freeUnits = maxFreeUnits
    }

    const totalRateWithoutDiscount = convertRupeeToPaisa(item.rate || 0) * item.quantity
    const lineItemDiscount = convertRupeeToPaisa(item.discount || 0) * item.quantity
    const pretaxTotal = totalRateWithoutDiscount - lineItemDiscount
    const cgstValue = billingRoundFunction(pretaxTotal * ((item.cgstTaxPercent || 0) / 100))
    const sgstValue = billingRoundFunction(pretaxTotal * ((item.sgstTaxPercent || 0) / 100))
    const igstValue = isIGST
      ? billingRoundFunction(pretaxTotal * ((item.igstPercent || 0) / 100))
      : 0
    const cessValue = billingRoundFunction(pretaxTotal * ((item.cessPercent || 0) / 100))
    const totalTax = cessValue + (isIGST ? igstValue : cgstValue + sgstValue)

    const lineItemAmount = pretaxTotal + totalTax
    return {
      catalog_item_id: item.itemId,
      quantity: item.quantity,
      amount: lineItemAmount,
      info: {
        sku: item.skuId || '',
        hsn_code: item.hsnCode || '',
        name: item.item,
        unit: allItemDetails?.unit || '',
        rate: convertRupeeToPaisa(item.rate || 0),
        mrp: convertRupeeToPaisa(item.mrp || 0),
        quantity: item.quantity,
        tax: totalTax,
        discount: lineItemDiscount,
        amount: lineItemAmount,
        tax_components: getTaxComponents(item, cgstValue, sgstValue, igstValue, cessValue, isIGST),
        ...(item.hsnCode && { hsn_code: item.hsnCode }),
        catalog_item_id: item.itemId,
        ...(unitOffers.length && {
          unit_offers: unitOffers
        }),
        ...(hasFreeUnits && freeUnits > 0 && { free_quantity: freeUnits }),
        notes: item.notes,
        qtt_props: item?.qtt_props
      }
    }
  })
}

export const getUpdatedCartValues = (
  cartItem: ICartItem,
  item: IStaffInventoryItems,
  customerDetails: any,
  applyAllApplicableOffers?: boolean
) => {
  const { gstTaxPercent = 0, cessPercent = 0 } = item
  const { discount: calcDiscount = 0, appliedOfferId = '' } = chooseBestOffer(
    item.applicableOfferDetails,
    cartItem.rate,
    cartItem.quantity,
    customerDetails,
    applyAllApplicableOffers
  )
  const effectiveRate = cartItem.rate - calcDiscount
  const effectiveRateInPaisa = convertRupeeToPaisa(effectiveRate)
  const netRateInPaisa = effectiveRateInPaisa * (1 + (gstTaxPercent + cessPercent) / 100)
  const taxAmountInPaisa = netRateInPaisa - effectiveRateInPaisa
  return {
    unitNetRate: convertPaisaToRupee(billingRoundFunction(netRateInPaisa)),
    tax: {
      percentage: (item.gstTaxPercent || 0) + (item.cessPercent || 0),
      amount: convertPaisaToRupee(billingRoundFunction(taxAmountInPaisa))
    },
    amount: cartItem?.quantity * netRateInPaisa,
    rateAmount: cartItem?.quantity * effectiveRateInPaisa,
    discount: {
      name: item.applicableOfferDetails?.find((_) => _.id === appliedOfferId)?.name || '',
      amount: calcDiscount
    }
  }
}

export const getUpdatedItemValues = (
  item: IStaffInventoryItems,
  customerDetails: any,
  applyAllApplicableOffers?: boolean
) => {
  const { gstTaxPercent = 0, cessPercent = 0 } = item
  const { discount: calcDiscount = 0, appliedOfferId = '' } = chooseBestOffer(
    item.applicableOfferDetails,
    item.rate || 0,
    item.quantity,
    customerDetails,
    applyAllApplicableOffers
  )
  const effectiveRate = (item.rate || 0) - calcDiscount
  const effectiveRateInPaisa = convertRupeeToPaisa(effectiveRate)
  const netRateInPaisa = effectiveRateInPaisa * (1 + (gstTaxPercent + cessPercent) / 100)
  const taxAmountInPaisa = netRateInPaisa - effectiveRateInPaisa
  return {
    unitNetRate: convertPaisaToRupee(billingRoundFunction(netRateInPaisa)),
    tax: {
      percentage: (item.gstTaxPercent || 0) + (item.cessPercent || 0),
      amount: convertPaisaToRupee(billingRoundFunction(taxAmountInPaisa))
    },
    amount: item?.quantity * netRateInPaisa,
    rateAmount: item?.quantity * effectiveRateInPaisa,
    appliedOfferId,
    discount: {
      name: item.applicableOfferDetails?.find((_) => _.id === appliedOfferId)?.name || '',
      amount: calcDiscount
    }
  }
}
